import React, { useEffect, useRef, useState } from "react";
import { Autocomplete, TextField } from "@mui/material";
import { suggestRegMSecurities } from "../../../services/Axios";
import { prepareSuggestions, shouldSuggest } from "./regmFormat";

const DEBOUNCE_MS = 300;

// Free-text security box with optional suggestions (not a whitelist: typed text is used as is).
// onTextChange(text) fires on every keystroke; onChoose(text) fires when a suggestion is chosen.
// onSubmit(text, exact) fires on Enter (exact is true when Enter selected a highlighted suggestion, otherwise undefined so the caller uses its current mode).
const SecuritySuggest = ({ onTextChange, onChoose, onSubmit, disabled, ariaLabel, placeholder, error, helperText, autoFocus = false }) => {
  const [text, setText] = useState("");
  const [options, setOptions] = useState([]);
  const requestCounter = useRef(0);
  const timer = useRef(null);
  const highlighted = useRef(null);
  const enterSelects = useRef(false);

  useEffect(
    () => () => {
      clearTimeout(timer.current);
      requestCounter.current += 1;
    },
    [],
  );

  const handleInput = (value, chosen = false) => {
    setText(value);
    highlighted.current = null;
    enterSelects.current = false;
    if (chosen) onChoose(value);
    else onTextChange(value);
    clearTimeout(timer.current);
    requestCounter.current += 1;
    const myId = requestCounter.current;
    if (!shouldSuggest(value)) {
      setOptions([]);
      return;
    }
    timer.current = setTimeout(async () => {
      try {
        const rows = await suggestRegMSecurities(value.trim());
        if (myId !== requestCounter.current) return;
        setOptions(prepareSuggestions(rows));
      } catch (error) {
        // Quiet: typing still works without suggestions.
        if (myId === requestCounter.current) setOptions([]);
      }
    }, DEBOUNCE_MS);
  };

  return (
    <Autocomplete
      freeSolo
      disabled={disabled}
      options={options}
      // The server already filtered; do not re-filter by the typed text.
      filterOptions={(x) => x}
      getOptionLabel={(o) => (typeof o === "string" ? o : o.value)}
      isOptionEqualToValue={(a, b) => a.value === b.value}
      inputValue={text}
      onInputChange={(event, value, reason) => {
        // "reset" is MUI echoing a selection; the choice is handled in onChange.
        if (reason === "reset") return;
        handleInput(value);
      }}
      onChange={(event, option) => {
        if (option && typeof option !== "string") {
          const submitAfter = enterSelects.current;
          handleInput(option.value, true);
          // Close the list and stop pending lookups for the chosen text.
          clearTimeout(timer.current);
          requestCounter.current += 1;
          setOptions([]);
          // Enter on a highlighted option selects it (exact) and then checks it.
          if (submitAfter) onSubmit(option.value, true);
        }
        enterSelects.current = false;
      }}
      onHighlightChange={(event, option) => {
        highlighted.current = option;
      }}
      onClose={() => {
        highlighted.current = null;
      }}
      renderOption={(props, option) => {
        const { key, ...rest } = props;
        return (
          <li key={key} {...rest}>
            {option.label}
          </li>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          size="small"
          placeholder={placeholder}
          autoFocus={autoFocus}
          error={error}
          helperText={helperText}
          inputProps={{ ...params.inputProps, "aria-label": ariaLabel }}
          onKeyDown={(event) => {
            if (event.key !== "Enter") {
              enterSelects.current = false;
              return;
            }
            if (highlighted.current) {
              // MUI selects the option; onChange then submits it.
              enterSelects.current = true;
              return;
            }
            // Free text: submit what is visible (MUI's own Enter handling is ignored).
            event.preventDefault();
            onSubmit(text);
          }}
        />
      )}
    />
  );
};

export default SecuritySuggest;
